import type { MsaColorScheme } from "@components/msa-viewer/types";
import { type ComplementMode, db, defaultViewerDefaults } from "@db";
import { updateSettings } from "@db/settings";
import { useAutoSave } from "@hooks/use-auto-save";
import { GENETIC_CODE_OPTIONS, type GeneticCodeId } from "@lib/geneticCodes";
import type { Frame } from "@lib/genetics";
import { createFileRoute } from "@tanstack/react-router";
import { Checkbox } from "@ui/checkbox";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";
import { useLiveQuery } from "dexie-react-hooks";
import { useCallback } from "react";

export const Route = createFileRoute("/settings/viewers")({
  component: ViewersSettingsComponent,
});

function ViewersSettingsComponent() {
  const settings = useLiveQuery(() => db.settings.get("settings"));

  if (!settings) {
    return null;
  }

  const defaults = settings.viewerDefaults ?? defaultViewerDefaults;

  return <ViewersSettings defaults={defaults} />;
}

function useViewerAutoSave<T>(path: string) {
  const save = useCallback(
    async (value: T) => {
      await updateSettings({ [`viewerDefaults.${path}`]: value });
    },
    [path],
  );

  return useAutoSave<T>({ save });
}

function ViewersSettings({
  defaults,
}: {
  defaults: typeof defaultViewerDefaults;
}) {
  const showFeatures = useViewerAutoSave<boolean>("sequence.showFeatures");
  const showRestrictionSites = useViewerAutoSave<boolean>(
    "sequence.showRestrictionSites",
  );
  const sequenceShowGcContent = useViewerAutoSave<boolean>(
    "sequence.showGcContent",
  );
  const sequenceShowSequenceGaps = useViewerAutoSave<boolean>(
    "sequence.showSequenceGaps",
  );
  const complementMode = useViewerAutoSave<ComplementMode>(
    "sequence.complementMode",
  );
  const translationFrames = useViewerAutoSave<Frame[]>(
    "sequence.translationFrames",
  );
  const geneticCodeId = useViewerAutoSave<number>("sequence.geneticCodeId");
  const visibleTracks = useViewerAutoSave<string[]>("msa.visibleTracks");
  const msaColorScheme = useViewerAutoSave<MsaColorScheme | undefined>(
    "msa.colorScheme",
  );
  const collectionShowGcContent = useViewerAutoSave<boolean>(
    "collection.showGcContent",
  );

  return (
    <div className="max-w-xl p-6">
      <div>
        <FieldSet>
          <FieldLegend>Sequence Viewer</FieldLegend>
          <FieldDescription className="-mt-2">
            Default track visibility for the sequence viewer.
          </FieldDescription>
          <FieldGroup>
            <Field orientation="horizontal">
              <Checkbox
                id="showFeatures"
                checked={defaults.sequence.showFeatures}
                onCheckedChange={(checked) =>
                  showFeatures.onChange(checked === true)
                }
              />
              <FieldLabel htmlFor="showFeatures">Show features</FieldLabel>
            </Field>
            <Field orientation="horizontal">
              <Checkbox
                id="showRestrictionSites"
                checked={defaults.sequence.showRestrictionSites}
                onCheckedChange={(checked) =>
                  showRestrictionSites.onChange(checked === true)
                }
              />
              <FieldLabel htmlFor="showRestrictionSites">
                Show restriction sites
              </FieldLabel>
            </Field>
            <Field orientation="horizontal">
              <Checkbox
                id="sequenceShowGcContent"
                checked={defaults.sequence.showGcContent}
                onCheckedChange={(checked) =>
                  sequenceShowGcContent.onChange(checked === true)
                }
              />
              <FieldLabel htmlFor="sequenceShowGcContent">
                Show GC content
              </FieldLabel>
            </Field>
            <Field orientation="horizontal">
              <Checkbox
                id="sequenceShowSequenceGaps"
                checked={defaults.sequence.showSequenceGaps}
                onCheckedChange={(checked) =>
                  sequenceShowSequenceGaps.onChange(checked === true)
                }
              />
              <FieldLabel htmlFor="sequenceShowSequenceGaps">
                Show gaps between 10-base groups
              </FieldLabel>
            </Field>
            <Field>
              <FieldLabel htmlFor="complementMode">Complement mode</FieldLabel>
              <Select
                value={defaults.sequence.complementMode}
                onValueChange={(v) =>
                  complementMode.onChange(v as ComplementMode)
                }
              >
                <SelectTrigger id="complementMode" className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="dna">DNA</SelectItem>
                  <SelectItem value="rna">RNA</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Translation frames</FieldLabel>
              <div className="flex flex-wrap gap-4">
                {([1, 2, 3, -1, -2, -3] as Frame[]).map((frame) => (
                  <Field key={frame} orientation="horizontal">
                    <Checkbox
                      id={`frame-${frame}`}
                      checked={defaults.sequence.translationFrames.includes(
                        frame,
                      )}
                      onCheckedChange={(checked) => {
                        const current = defaults.sequence.translationFrames;
                        if (checked) {
                          translationFrames.onChange(
                            [...current, frame].sort((a, b) => a - b),
                          );
                        } else {
                          translationFrames.onChange(
                            current.filter((f) => f !== frame),
                          );
                        }
                      }}
                    />
                    <FieldLabel htmlFor={`frame-${frame}`}>
                      {frame > 0 ? `+${frame}` : frame}
                    </FieldLabel>
                  </Field>
                ))}
              </div>
            </Field>
            <Field>
              <FieldLabel htmlFor="geneticCode">Genetic code</FieldLabel>
              <Select
                value={String(defaults.sequence.geneticCodeId)}
                onValueChange={(v) =>
                  geneticCodeId.onChange(Number(v) as GeneticCodeId)
                }
              >
                <SelectTrigger id="geneticCode" className="w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GENETIC_CODE_OPTIONS.map((code) => (
                    <SelectItem key={code.id} value={String(code.id)}>
                      {code.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </FieldGroup>
        </FieldSet>

        <FieldSet>
          <FieldLegend>MSA Viewer</FieldLegend>
          <FieldDescription className="-mt-2">
            Default track visibility for the multiple sequence alignment viewer.
          </FieldDescription>
          <FieldGroup>
            {[
              { id: "consensus", label: "Consensus" },
              { id: "identity", label: "Identity" },
              { id: "gap", label: "Gap frequency" },
              { id: "entropy", label: "Entropy" },
              { id: "logo", label: "Sequence logo" },
            ].map((track) => (
              <Field key={track.id} orientation="horizontal">
                <Checkbox
                  id={`msa-${track.id}`}
                  checked={defaults.msa.visibleTracks.includes(track.id)}
                  onCheckedChange={(checked) => {
                    const current = defaults.msa.visibleTracks;
                    if (checked) {
                      visibleTracks.onChange([...current, track.id]);
                    } else {
                      visibleTracks.onChange(
                        current.filter((t) => t !== track.id),
                      );
                    }
                  }}
                />
                <FieldLabel htmlFor={`msa-${track.id}`}>
                  {track.label}
                </FieldLabel>
              </Field>
            ))}
            <Field>
              <FieldLabel htmlFor="msaColorScheme">
                Default color scheme
              </FieldLabel>
              <Select
                value={defaults.msa.colorScheme ?? "auto"}
                onValueChange={(v) =>
                  msaColorScheme.onChange(
                    v === "auto" ? undefined : (v as MsaColorScheme),
                  )
                }
              >
                <SelectTrigger id="msaColorScheme" className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto</SelectItem>
                  <SelectItem value="nucleotide">Nucleotide</SelectItem>
                  <SelectItem value="property">Property</SelectItem>
                  <SelectItem value="clustalx">ClustalX</SelectItem>
                  <SelectItem value="zappo">Zappo</SelectItem>
                  <SelectItem value="taylor">Taylor</SelectItem>
                  <SelectItem value="conservation">Conservation</SelectItem>
                  <SelectItem value="gaps">Highlight Gaps</SelectItem>
                  <SelectItem value="agreement">Agreement</SelectItem>
                  <SelectItem value="none">No color</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </FieldGroup>
        </FieldSet>

        <FieldSet>
          <FieldLegend>Collection Viewer</FieldLegend>
          <FieldDescription className="-mt-2">
            Default track visibility for the sequence collection viewer.
          </FieldDescription>
          <FieldGroup>
            <Field orientation="horizontal">
              <Checkbox
                id="collectionShowGcContent"
                checked={defaults.collection.showGcContent}
                onCheckedChange={(checked) =>
                  collectionShowGcContent.onChange(checked === true)
                }
              />
              <FieldLabel htmlFor="collectionShowGcContent">
                Show GC content
              </FieldLabel>
            </Field>
          </FieldGroup>
        </FieldSet>
      </div>
    </div>
  );
}
